import {Emit_main} from "../navigation"
import React, {memo, useCallback, BaseSyntheticEvent, useState, useRef, useEffect} from "react"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faPlus, faPencil, IconDefinition, faTrash, faSyncAlt} from "@fortawesome/free-solid-svg-icons"
import {faFacebook, faLinkedin, faTiktok, faXTwitter} from "@fortawesome/free-brands-svg-icons"
import {z} from "zod"
import {Link, NavigateFunction, useNavigate} from "react-router-dom"

import {Eres_card, Eres_iconbutton, Eres_info_badge, Eres_modal,
    Eres_header2xl, Eres_spin_placeholder, stop_propagation, Eres_headerlg,
    nosort} from "../controls"

import {connected_network_tag, connected_network, program_state, user_state,
    result, Err, Ok, unreachable, v1api, authn_token} from "../program-state"

import * as C from "../controls"
import * as P from "../program-state"

type network_info = { icon : IconDefinition, display_name : string, tag : string, oauth_url : P.raw_fragment, tailwind_color : string, mk_delete_url : (eres_dbid : string) => string }
const _n = (icon : IconDefinition, display_name : string, tag : string, oauth_url : P.raw_fragment, tailwind_color : string, mk_delete_url : (eres_dbid : string) => string) : network_info => {
    return {icon, display_name, tag, oauth_url, tailwind_color, mk_delete_url}
}
type ninfo_t = Record<connected_network_tag, network_info>
const _ninfo : ninfo_t = {
    "nw-facebook" : _n(faFacebook, "Facebook", "fb", P.rf("oauth/initialize-facebook-oauth?redirect=false"), "tw-text-facebook-blue", id => v1api`social/facebook/users/${id}`),
    "nw-linkedin" : _n(faLinkedin, "Linkedin", "li", P.rf("oauth/initialize-linkedin-oauth?redirect=false"), "tw-text-linkedin-blue", id => v1api`social/linkedin/users/${id}`),
    "nw-tiktok"   : _n(faTiktok,   "Tiktok",   "tt", P.rf("oauth/initialize-tiktok-oauth?redirect=false"),   "tw-text-black",         id => v1api`social/tiktok/users/${id}`),
    "nw-x"        : _n(faXTwitter, "X",        "x",  P.rf("oauth/initialize-x-oauth?redirect=false"),        "tw-text-black",         id => v1api`social/x/users/${id}`),
}
const ninfo = (network : connected_network_tag) : network_info => _ninfo[network]

type header_description = C.header_description<connected_network>

 export const cn_to_edit_href = (item : connected_network) => {
    const {tag} = ninfo(item.network_tag)
    return `/connected-networks/${tag}-${item.eres_dbid}`
}

const _Emit_table_row = ({item} : {item : connected_network}) => {
    const edit_url = cn_to_edit_href(item)
    const router = useNavigate()
    const onclick_closure = useCallback((e : BaseSyntheticEvent<any>) => {
        e.stopPropagation()
        const sel = window.getSelection()
        if (sel !== null) if (!sel.isCollapsed) return
        router(edit_url)
    }, [])
    const {icon, display_name} = ninfo(item.network_tag)
    let refresh_elt : React.ReactElement | null = null
    switch (item.network_tag) {
    // XXXih: FIXME: need to handle this correctly for multiple facebook accounts
    case "nw-facebook": refresh_elt = (<a href={v1api`oauth/initialize-facebook-oauth?redirect=false`} onClick={stop_propagation}><FontAwesomeIcon icon={faSyncAlt} area-hidden="true" className="tw-pe-2"/></a>); break
    case "nw-linkedin": refresh_elt = null; break
    case "nw-tiktok": refresh_elt = null; break
    case "nw-x": refresh_elt = (<a href={v1api`oauth/initialize-x-oauth?redirect=false`} onClick={stop_propagation}><FontAwesomeIcon icon={faSyncAlt} area-hidden="true" className="tw-pe-2"/></a>); break
    default: unreachable(item.network_tag)
    }
    return (
    <tr key={item.eres_dbid} className="tw-border-y tw-border-x-0 last:tw-border-b-0 hover:tw-bg-gray-50 hover:tw-cursor-pointer" onClick={onclick_closure}>
        <C.Eres_basic_tablecol>
            <div className="tw-text-base tw-text-big-stone-950 tw-font-semibold">{item.name}</div>
            <p className="tw-text-xs">{item.network_account_id}</p>
        </C.Eres_basic_tablecol>
        <C.Eres_basic_tablecol>
            <FontAwesomeIcon icon={icon} aria-hidden="true" className="pe-1"/>
            {display_name}
        </C.Eres_basic_tablecol>
        <C.Eres_basic_tablecol>
            <Eres_info_badge>Valid</Eres_info_badge>
        </C.Eres_basic_tablecol>
        <C.Eres_basic_tablecol>
            {local_datetime_locale.format(item.creation_time)}
        </C.Eres_basic_tablecol>
        <C.Eres_basic_tablecol align="right">
            {refresh_elt}
            <Link to={edit_url} onClick={stop_propagation}><FontAwesomeIcon icon={faPencil} aria-hidden="true" className="tw-pe-2"/></Link>
            <a href="#" onClick={stop_propagation}><FontAwesomeIcon icon={faTrash} aria-hidden="true"/></a>
        </C.Eres_basic_tablecol>
    </tr>)
}

const Emit_table_row = memo(_Emit_table_row)

const local_datetime_locale = new Intl.DateTimeFormat()

const english_collator = new Intl.Collator("en")

const compare_network_tag = (x : connected_network, y : connected_network) => english_collator.compare(x.network_tag, y.network_tag)
const compare_name = (x : connected_network, y : connected_network) => x.name.localeCompare(y.name)
const compare_creation_time = (x : connected_network, y : connected_network) => x.creation_time - y.creation_time
type cmpfn = C.cmpfn<connected_network>
const neg = (x : number) : number => -x
const idn = (x : number) : number => x

const headers = C.eres_prepare_table<connected_network>([
    ["name"         , "Name (ID)"  , "50%" , compare_name],
    ["network"      , "Network"    , "25%" , compare_network_tag],
    ["status"       , "Status"     , "12%" , nosort],
    ["added"        , "Added"      , "12%" , compare_creation_time],
    ["quickbuttons" , " "          , "1%"  , nosort],
])

type cnprops = {
    sort_order? : string,
}

const parse_sort_direction = (s : string) : result<[C.ascdesc_t, string], null> => {
    if (s.startsWith("desc-")) return [Ok, [C.desc, s.slice(5)]]
    if (s.startsWith("asc-")) return [Ok, [C.asc, s.slice(4)]]
    return [Err, null]
}

const parse_sort_order = (s : string) : [cmpfn, C.ascdesc_t, string] => {
    const [ok, v] = parse_sort_direction(s)
    let rest = "added"
    let sfn = compare_creation_time
    let ascdesc = idn
    let dir : C.ascdesc_t = C.asc
    if (ok) {
        [dir, rest] = v
        rest = v[1]
        const cmpfn = headers.sorts.get(rest)
        if (cmpfn !== undefined) sfn = cmpfn
        ascdesc =
            dir === C.asc ? idn :
            dir === C.desc ? neg :
            unreachable(dir)
    }
    return [(x : connected_network, y : connected_network) => ascdesc(sfn(x, y)), dir, rest]
}

const oauth_redirect_response = z.object({
    type : z.string(),
    uri : z.string(),
})
const get_oauth_uri_and_redirect = (api_path : P.raw_fragment, router : (url : string) => void, token : authn_token, network_tag? : connected_network_tag) => async (e : BaseSyntheticEvent<any>) => {
    e.preventDefault()
    const url = v1api`${api_path}`
    const [ok, resp] = await P.issue_json_get(url, oauth_redirect_response, token)
    if (!ok) {
        console.log("7d00ad55bfc73981ae29a80da46d49eb: unexpected failure", resp)
        return
    }
    // mark oauth start so we can poll after redirect
    if (network_tag === "nw-x") {
        sessionStorage.setItem('x_oauth_pending', 'true')
    }
    router(resp.uri)
}

export type netinfo = result<connected_network, undefined>
export type network_card_data = {network : connected_network_tag, token : authn_token, netinfo : result<connected_network, undefined>}
export type network_card_or_loading = ["loading", undefined] | ["ncard", network_card_data]
export const still_loading : network_card_or_loading = ["loading", undefined]

const Network_card = ({data, activate_delete_modal} : {data : network_card_or_loading, activate_delete_modal : (x : delete_modal_state) => void}) => {
    const handle_external_url = (url: string) => window.location.href = url
    const basic_card_style = "tw-h-96 tw-flex tw-flex-col tw-gap-3"
    const card_border_style = "tw-border tw-border-dashed"
    const [dtag, _data] = data
    const trigger_delete = useCallback(() => {
        switch (dtag) {
        case "loading":
            return
        case "ncard": {
            const { netinfo } = _data
            const [ok, ni] = netinfo
            if (ok) {
                activate_delete_modal({ tag : "delete-modal", netdata : _data, connected_network : ni })
            }
        } break
        default: unreachable(dtag)
        }
    }, [dtag, _data, activate_delete_modal])
    switch (dtag) {
    case "loading": return (<div className={`${basic_card_style} ${card_border_style}`}>
            <Eres_spin_placeholder className="tw-p-6"/>
        </div>)
    case "ncard": {
        const { network, token, netinfo } = _data
        const {icon, display_name, oauth_url, tailwind_color} = ninfo(network)
        const click_handler = get_oauth_uri_and_redirect(oauth_url, handle_external_url, token, network)
        const [ok, ni] = netinfo
        if (ok && ni.network_tag === network) {
            const edit_url = cn_to_edit_href(ni)
            return (
            <div className={basic_card_style}>
                <Link to={edit_url} className="tw-border tw-border-dashed tw-rounded tw-h-3/4 tw-justify-center tw-text-center tw-flex tw-flex-col hover:tw-bg-gray-50 tw-cursor-pointer">
                <FontAwesomeIcon icon={icon} className={`tw-h-32 ${tailwind_color}`} aria-hidden="true"/>
                </Link>
                <div>
                <Eres_headerlg>{ni.name}</Eres_headerlg>
                <p className="tw-text-sm">Account ID: {ni?.network_account_id}</p>
                <p className="tw-text-sm">Status: Connected</p>
                </div>
                <div className="tw-text-right">
                    <Link to={edit_url} className="tw-pe-2"><FontAwesomeIcon icon={faPencil} aria-hidden="true"/></Link>
                    <button onClick={trigger_delete}><FontAwesomeIcon icon={faTrash} aria-hidden="true"/></button>
                </div>
            </div>)
        } else {
            return (
            <>
            <div onClick={click_handler} className={`${basic_card_style} ${card_border_style} tw-text-center tw-rounded tw-justify-center hover:tw-bg-gray-50 tw-cursor-pointer`}>
                <h2 className="tw-text-lg tw-font-bold">{display_name}</h2>
                <FontAwesomeIcon icon={icon} className="tw-h-10" aria-hidden="true"/>
                Add {display_name} Account
            </div>
            </>)
        }
    }
    default: unreachable(dtag)
    }
}

export const find = <t,>(xs : t[], pred : (x : t) => boolean) : result<t, undefined> => {
    for (const x of xs) if (pred(x)) return [Ok, x]
    return [Err, undefined]
}

export const is_network = (net : connected_network_tag) => (cn : connected_network) : boolean => {
    return cn.network_tag === net
}

const get_network_assets = (brand : any, network : string) : string[] => {
    const assets : string[] = []
    switch(network) {
        case "facebook":
            if (brand.facebook_user_id && brand.facebook_user_id.length > 0) {
                assets.push("Facebook Connection")
            }
            break
        case "linkedin":
            if (brand.linkedin_user_id && brand.linkedin_user_id.length > 0) {
                assets.push("LinkedIn Connection")
            }
            break
        case "tiktok":
            if (brand.tiktok_user_id && brand.tiktok_user_id.length > 0) {
                assets.push("TikTok Connection")
            }
            break
    }
    return assets
}

const analyze_brand_for_deletion = (brand : any, network : string) : brand_conflict_info => {
    const platforms : string[] = []
    if (brand.facebook_user_id && brand.facebook_user_id.length > 0) platforms.push("Facebook")
    if (brand.linkedin_user_id && brand.linkedin_user_id.length > 0) platforms.push("LinkedIn")
    if (brand.tiktok_user_id && brand.tiktok_user_id.length > 0) platforms.push("TikTok")

    return {
        brand_id: brand.id,
        brand_name: brand.name,
        platforms,
        assets: get_network_assets(brand, network),
        selected: true
    }
}

type brand_conflict_info = {
    brand_id : number
    brand_name : string
    platforms : string[]
    assets : string[]
    selected : boolean
}

type delete_modal_state = {
    tag : "delete-modal",
    netdata : network_card_data,
    connected_network : connected_network,
    brand_conflicts? : brand_conflict_info[]
    is_processing? : boolean
    error_message? : string
}
type no_modal = { tag : "no-modal" }
const no_modal : no_modal = { tag : "no-modal" }

const _Emit_connected_networks = ({sort_order} : cnprops) => {
    const [connected_networks, ps, us] = P.use_connected_networks()
    const [modal_state, set_modal_state] = useState<no_modal | delete_modal_state>(no_modal)
    const modal_is_open_ref = useRef(false)
    const [is_polling_x_oauth, set_is_polling_x_oauth] = useState(false)

    useEffect(() => {
        modal_is_open_ref.current = modal_state.tag === "delete-modal"
    }, [modal_state.tag])

    // check if we're returning from x oauth and poll for connection
    useEffect(() => {
        const x_oauth_pending = sessionStorage.getItem('x_oauth_pending')

        if (x_oauth_pending === 'true' && !is_polling_x_oauth) {
            set_is_polling_x_oauth(true)

            // poll for x connection
            const poll_x_connection = async () => {
                const max_attempts = 30  
                const poll_interval = 1000 

                for (let attempt = 0; attempt < max_attempts; attempt++) {
                    const [ok, data] = await P.issue_json_get(
                        P.v1api`social/x/users`,
                        z.array(z.object({
                            id : z.number(),
                            x_id : z.string(),
                            screen_name : z.string(),
                        })),
                        us.token
                    )

                    if (ok && data && data.length > 0) {
                        // connection successful, refresh networks and clear flag
                        sessionStorage.removeItem('x_oauth_pending')
                        await P.poll_connected_networks(ps, us)
                        set_is_polling_x_oauth(false)
                        return
                    }

                    // wait before next poll
                    await new Promise(resolve => setTimeout(resolve, poll_interval))
                }

                // polling timed out
                console.log("X OAuth polling timed out")
                sessionStorage.removeItem('x_oauth_pending')
                set_is_polling_x_oauth(false)
            }

            void poll_x_connection()
        }
    }, [us.token, ps, us, is_polling_x_oauth])

    P.run_once_on_component_creation(() => void P.poll_connected_networks(ps, us))
    const is_updating = us.connected_networks_current_fetch_task !== null
    const [sort_fn, sort_dir, sort_id] = parse_sort_order(sort_order || "")
    let fbnet : network_card_or_loading = still_loading
    let linet : network_card_or_loading = still_loading
    let ttnet : network_card_or_loading = still_loading
    let xnet : network_card_or_loading = still_loading
    let table = <></>
    let delete_modal = null
    const dismiss_delete_modal = useCallback((e?: any) => {
        if (!modal_is_open_ref.current) {
            return
        }

        // ignore if this is being called without a user interaction (like from FocusTrap onDeactivate during re-render)
        if (e === undefined || e.type === undefined) {
            return
        }

        modal_is_open_ref.current = false
        set_modal_state(no_modal)
    }, [set_modal_state])
    if (connected_networks !== undefined) {
        const networks = connected_networks.toSorted(sort_fn)
        const fbnet_info = find(networks, is_network("nw-facebook"))
        const linet_info = find(networks, is_network("nw-linkedin"))
        const ttnet_info = find(networks, is_network("nw-tiktok"))
        const xnet_info = find(networks, is_network("nw-x"))
        fbnet = ["ncard", {network : "nw-facebook", token : us.token, netinfo : fbnet_info}]
        linet = ["ncard", {network : "nw-linkedin", token : us.token, netinfo : linet_info}]
        ttnet = ["ncard", {network : "nw-tiktok", token : us.token, netinfo : ttnet_info}]
        xnet = ["ncard", {network : "nw-x", token : us.token, netinfo : xnet_info}]
        table = true ? <></> : (
        <Eres_card>
        <div className="tw-mt-6 tw-px-6 tw-flex tw-flex-wrap">
            <div className="tw-flex-grow">
                <Eres_header2xl>Connected Networks</Eres_header2xl>
            </div>
            <div className="tw-flex tw-flex-grow-0">
                <Eres_iconbutton icon={faPlus}>Add Network</Eres_iconbutton>
            </div>
        </div>
        <div className="tw-h-6 tw-px-6 tw-py-0 tw-m-0">
            {is_updating
                ? <Eres_iconbutton className="tw-text-xs tw-py-0 tw-h-6">Updating...</Eres_iconbutton>
                : <Eres_iconbutton className="tw-text-xs tw-py-0 tw-h-6">Refresh</Eres_iconbutton>
            }
        </div>

        </Eres_card>
        )

        const process_brands_and_retry_delete = async (netinfo : network_info, eres_network_dbid : P.connected_network_dbid) => {
            if (modal_state.tag !== "delete-modal" || !modal_state.brand_conflicts) return

            const {mk_delete_url, tag} = netinfo
            const delete_url = mk_delete_url(eres_network_dbid)
            const network = tag === "fb" ? "facebook" : tag === "li" ? "linkedin" : tag === "tt" ? "tiktok" : "x"

            set_modal_state({
                ...modal_state,
                is_processing: true,
                error_message: undefined
            })

            try {
                const selected_brands = modal_state.brand_conflicts.filter(b => b.selected)

                for (const brand_info of selected_brands) {
                    // 1. get the full brand object
                    const brand_url = P.v1api`brands/${brand_info.brand_id}`
                    const [brand_ok, brand_data] = await P.issue_json_get(brand_url, z.any(), us.token)

                    if (!brand_ok) {
                        console.log("Failed to fetch brand:", brand_info.brand_id)
                        set_modal_state({
                            ...modal_state,
                            is_processing: false,
                            error_message: `Failed to fetch brand ${brand_info.brand_name}`
                        })
                        return
                    }

                    // 2. update with network fields set to null
                    const updates : Record<string, any> = {...brand_data}

                    switch(network) {
                        case "facebook":
                            updates.meta_page = null
                            updates.ad_account_id = null
                            updates.meta_pixel_id = null
                            updates.instagram_asset_id = null
                            break
                        case "linkedin":
                            updates.linkedin_org = null
                            updates.linkedin_ad_account = null
                            break
                        case "tiktok":
                            updates.tiktok_user_id = null
                            updates.tiktok_identity_id = null
                            updates.tiktok_ad_account_id = null
                            updates.tiktok_pixel_id = null
                            break
                        case "x":
                            updates.x_ad_account_id = null
                            updates.x_pixel_id = null
                            break
                    }

                    // fix validation issues: if facebook page exists without ad account, remove the page
                    if (updates.meta_page !== null && updates.ad_account_id === null) {
                        updates.meta_page = null
                    }

                    const [update_ok, update_resp] = await P.issue_json_put(brand_url, updates, z.any(), us.token)

                    if (!update_ok) {
                        console.log("Failed to update brand:", brand_info.brand_id, update_resp)
                        set_modal_state({
                            ...modal_state,
                            is_processing: false,
                            error_message: `Failed to unlink brand ${brand_info.brand_name}`
                        })
                        return
                    }
                }

                // 3. retry deletion
                const [ok, resp] = await P.issue_json_delete(delete_url, z.any(), us.token)

                if (!ok) {
                    set_modal_state({
                        ...modal_state,
                        is_processing: false,
                        error_message: "Failed to delete account after processing brands"
                    })
                    return
                }

                // success
                P.update_state(ps, (us : user_state) => {
                    if (us.connected_networks === undefined) return
                    const idix = us.connected_networks.findIndex(cn => cn.eres_dbid === eres_network_dbid)
                    if (idix !== -1) us.connected_networks.splice(idix, 1)
                })
                modal_is_open_ref.current = false
                set_modal_state(no_modal)
            } catch (error) {
                set_modal_state({
                    ...modal_state,
                    is_processing: false,
                    error_message: "An error occurred while processing brands"
                })
            }
        }

        const actually_delete_network = (netinfo : network_info, eres_network_dbid : P.connected_network_dbid) => async () => {
            if (modal_state.tag !== "delete-modal") return

            const {mk_delete_url, tag} = netinfo
            const delete_url = mk_delete_url(eres_network_dbid)
            const network = tag === "fb" ? "facebook" : tag === "li" ? "linkedin" : tag === "tt" ? "tiktok" : "x"

            // set processing state
            set_modal_state({
                ...modal_state,
                is_processing: true,
                error_message: undefined
            })

            try {
                const [ok, resp] = await P.issue_json_delete(delete_url, z.any(), us.token)

                if (!ok) {
                    // check if it's a 403 with brand conflicts
                    if (resp instanceof P.http_error) {
                        const response = resp.resp

                        // parse the response body
                        try {
                            const error_data = await response.json()

                            if (error_data && error_data.detail && error_data.detail.brands) {
                                const brand_conflicts = error_data.detail.brands.map((brand : any) =>
                                    analyze_brand_for_deletion(brand, network)
                                )
                                set_modal_state({
                                    ...modal_state,
                                    brand_conflicts,
                                    is_processing: false
                                })
                                return
                            }

                            // other error
                            set_modal_state({
                                ...modal_state,
                                is_processing: false,
                                error_message: error_data?.detail?.message || "Failed to delete account"
                            })
                            return
                        } catch (e) {
                            console.log("Error parsing response:", e)
                            set_modal_state({
                                ...modal_state,
                                is_processing: false,
                                error_message: "Failed to delete account"
                            })
                            return
                        }
                    }

                    // non-http error
                    set_modal_state({
                        ...modal_state,
                        is_processing: false,
                        error_message: "Failed to delete account"
                    })
                    return
                }

                // success - remove from ui and close modal
                P.update_state(ps, (us : user_state) => {
                    if (us.connected_networks === undefined) return
                    const idix = us.connected_networks.findIndex(cn => cn.eres_dbid === eres_network_dbid)
                    if (idix !== -1) us.connected_networks.splice(idix, 1)
                })
                modal_is_open_ref.current = false
                set_modal_state(no_modal)
            } catch (error) {
                set_modal_state({
                    ...modal_state,
                    is_processing: false,
                    error_message: "An unexpected error occurred"
                })
            }
        }
        switch (modal_state.tag) {
        case "no-modal": break
        case "delete-modal": {
            const cn = modal_state.connected_network
            const netinfo = ninfo(modal_state.netdata.network)
            const {icon, display_name} = netinfo
            const has_conflicts = modal_state.brand_conflicts && modal_state.brand_conflicts.length > 0

            const toggle_brand_selection = (brand_id : number) => {
                if (modal_state.tag !== "delete-modal" || !modal_state.brand_conflicts) return
                const updated_conflicts = modal_state.brand_conflicts.map(b =>
                    b.brand_id === brand_id ? {...b, selected: !b.selected} : b
                )
                set_modal_state({
                    ...modal_state,
                    brand_conflicts: updated_conflicts
                })
            }

            let modal_content : React.ReactElement
            let button_strip : React.ReactElement

            if (has_conflicts) {
                const selected_count = modal_state.brand_conflicts!.filter(b => b.selected).length
                button_strip = (
                    <div className="tw-flex tw-gap-1.5 tw-items-center tw-flex-row-reverse">
                        <Eres_iconbutton
                            tabIndex={2}
                            icon={faTrash}
                            onClick={() => process_brands_and_retry_delete(netinfo, cn.eres_dbid)}
                            className="tw-bg-danger-bg hover:tw-bg-danger-hoverbg"
                            disabled={modal_state.is_processing || selected_count === 0}>
                            {modal_state.is_processing ? "Processing..." : "Continue"}
                        </Eres_iconbutton>
                        <Eres_iconbutton tabIndex={1} icon={icon} onClick={dismiss_delete_modal} disabled={modal_state.is_processing}>
                            Cancel
                        </Eres_iconbutton>
                    </div>
                )

                modal_content = (
                    <div className="tw-space-y-4">
                        <p className="tw-text-sm tw-text-gray-700">
                            This {display_name} account provides assets to {modal_state.brand_conflicts!.length} brand{modal_state.brand_conflicts!.length > 1 ? "s" : ""}. Select which brands to process:
                        </p>
                        {modal_state.error_message && (
                            <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded tw-p-3 tw-text-sm tw-text-red-700">
                                {modal_state.error_message}
                            </div>
                        )}
                        <div className="tw-space-y-3">
                            {modal_state.brand_conflicts!.map(brand => (
                                <div key={brand.brand_id} className="tw-border tw-rounded tw-p-3">
                                    <div className="tw-flex tw-items-start tw-gap-3">
                                        <input
                                            type="checkbox"
                                            checked={brand.selected}
                                            onChange={() => toggle_brand_selection(brand.brand_id)}
                                            className="tw-mt-1 tw-w-4 tw-h-4"
                                            disabled={modal_state.is_processing}
                                        />
                                        <div className="tw-flex-1">
                                            <div className="tw-font-semibold tw-text-sm">{brand.brand_name}</div>
                                            <div className="tw-text-xs tw-text-gray-600 tw-mt-1">
                                                {display_name}: {brand.assets.join(", ")}
                                            </div>
                                            {brand.platforms.length > 1 && (
                                                <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                                    Other platforms: {brand.platforms.filter(p => p !== display_name).join(", ")}
                                                </div>
                                            )}
                                            <div className="tw-text-xs tw-font-medium tw-mt-2 tw-text-gray-700">
                                                Will unlink {display_name} assets from this brand
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            } else {
                button_strip = (
                    <div className="tw-flex tw-gap-1.5 tw-items-center tw-flex-row-reverse">
                        <Eres_iconbutton
                            tabIndex={2}
                            icon={faTrash}
                            onClick={actually_delete_network(netinfo, cn.eres_dbid)}
                            className="tw-bg-danger-bg hover:tw-bg-danger-hoverbg"
                            disabled={modal_state.is_processing}>
                            {modal_state.is_processing ? "Disconnecting..." : `Disconnect ${display_name}`}
                        </Eres_iconbutton>
                        <Eres_iconbutton tabIndex={1} icon={icon} onClick={dismiss_delete_modal} disabled={modal_state.is_processing}>
                            Keep {display_name}
                        </Eres_iconbutton>
                    </div>
                )

                modal_content = (
                    <div>
                        {modal_state.error_message && (
                            <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded tw-p-3 tw-text-sm tw-text-red-700 tw-mb-4">
                                {modal_state.error_message}
                            </div>
                        )}
                        <p>This currently cannot be undone. Once you unlink your {display_name} account, you'll have to repeat the connection process to re-link Eres with {display_name}.</p>
                    </div>
                )
            }

            delete_modal = (
                <Eres_modal
                    button_strip={button_strip}
                    title={has_conflicts ? `Cannot Delete ${display_name} Account` : `Are you sure you want to unlink ${display_name}?`}
                    on_cancel={dismiss_delete_modal}>
                    {modal_content}
                </Eres_modal>
            )
        } break
        default: unreachable(modal_state)
        }
    }

    return (<>
    {delete_modal}
    <Eres_card>
    <div className="tw-mt-6 tw-mb-3 tw-px-6 tw-flex tw-flex-wrap">
        <div className="tw-flex-grow">
            <Eres_header2xl>Connected Networks</Eres_header2xl>
        </div>
    </div>
    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3 tw-px-6 tw-mb-6">
        <Network_card data={fbnet} activate_delete_modal={set_modal_state}/>
        <Network_card data={linet} activate_delete_modal={set_modal_state}/>
        <Network_card data={ttnet} activate_delete_modal={set_modal_state}/>
        <Network_card data={xnet} activate_delete_modal={set_modal_state}/>
    </div>
    </Eres_card>
    {table}
    </>
    )
}

const Emit_connected_networks = memo(_Emit_connected_networks)
export default function Connected_networks_page() {
    const query_params = "" // useSearchParams()
    // const _sort_order = query_params.getAll("cn-sort")
    const _sort_order = ""
    let sort_order : string | undefined = undefined
    if (_sort_order.length == 1) sort_order = _sort_order[0]
    return (<Emit_main page_id="PAGE-connected-networks">
        <Emit_connected_networks sort_order={sort_order}/>
        </Emit_main>)
}
